<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { SharePayload, ShareSite, ShareTrackEvent } from './types'
import { buildShareText, channelLinks, safeShareUrl } from './channels'
import { copyText } from './clipboard'
import { renderSharePoster } from './poster'
import { drawQr } from './qr'

const props = defineProps<{
  site: ShareSite
  payload: SharePayload
}>()

const emit = defineEmits<{
  close: []
  track: [event: ShareTrackEvent]
}>()

const includeSecret = ref(false)
const toast = ref('')
const posterUrl = ref('')
const posterBusy = ref(false)
const wechatOpen = ref(false)
const qrCanvas = ref<HTMLCanvasElement | null>(null)

const themeStyle = computed(() => {
  const theme = props.site.theme
  return {
    '--sk-bg': theme.bg,
    '--sk-surface': theme.surface,
    '--sk-text': theme.text,
    '--sk-muted': theme.muted,
    '--sk-primary': theme.primary,
    '--sk-accent': theme.accent,
    '--sk-border': theme.border,
    '--sk-paper': theme.paper,
    '--sk-font': theme.fontFamily,
  }
})

const preview = computed(() => buildShareText(props.payload, props.site, includeSecret.value))
const links = computed(() => channelLinks(props.payload, props.site, includeSecret.value))
const hasSecret = computed(() => Boolean(props.payload.secret?.trim()))
const canNative = computed(() => typeof navigator !== 'undefined' && typeof navigator.share === 'function')

const wechatMode = computed(() => {
  const ua = navigator.userAgent || ''
  if (/MicroMessenger/i.test(ua)) return 'in-app'
  if (/Android|iPhone|iPad|Mobile/i.test(ua)) return 'mobile'
  return 'desktop'
})

watch(
  () => props.payload,
  () => {
    includeSecret.value = false
    posterUrl.value = ''
    wechatOpen.value = false
    toast.value = ''
  },
)

watch(includeSecret, () => {
  posterUrl.value = ''
})

function showToast(message: string) {
  toast.value = message
  window.setTimeout(() => {
    if (toast.value === message) toast.value = ''
  }, 2200)
}

function track(channel: string) {
  const url = safeShareUrl(props.payload.url) || props.payload.url
  emit('track', { channel, url })
}

async function onCopy() {
  const ok = await copyText(preview.value)
  track('copy')
  showToast(ok ? '文案已复制' : '复制失败，请手动选择文案')
}

function onChannel(id: string, href: string | null) {
  track(id)
  if (id === 'wechat') {
    wechatOpen.value = true
    if (wechatMode.value === 'desktop') {
      void nextTick().then(paintQr)
    }
    return
  }
  if (!href) return
  window.open(href, '_blank', 'noopener,noreferrer')
}

function paintQr() {
  const canvas = qrCanvas.value
  const url = safeShareUrl(props.payload.url)
  if (!canvas || !url) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  drawQr(ctx, url, 0, 0, canvas.width)
}

async function onPoster() {
  if (posterBusy.value) return
  posterBusy.value = true
  try {
    posterUrl.value = await renderSharePoster(props.payload, props.site, includeSecret.value)
    track('poster')
  } catch {
    showToast('海报生成失败')
  } finally {
    posterBusy.value = false
  }
}

function downloadPoster() {
  if (!posterUrl.value) return
  const link = document.createElement('a')
  link.href = posterUrl.value
  link.download = `${props.site.name}-share.png`
  link.click()
  track('poster-download')
}

async function onNative() {
  if (!navigator.share) return
  track('native')
  try {
    await navigator.share({
      title: props.payload.headline,
      text: preview.value,
      url: safeShareUrl(props.payload.url) || undefined,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return
    showToast('系统分享不可用，请复制文案')
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('close')
}
</script>

<template>
  <div class="sk-mask" :style="themeStyle" @click.self="emit('close')" @keydown="onKeydown">
    <div class="sk-dialog" role="dialog" aria-modal="true" :aria-label="payload.modalTitle">
      <header class="sk-head">
        <h2>{{ payload.modalTitle }}</h2>
        <button type="button" class="sk-icon" aria-label="关闭" @click="emit('close')">×</button>
      </header>
      <p class="sk-notice">{{ site.notice }}</p>
      <label v-if="hasSecret" class="sk-check">
        <input v-model="includeSecret" type="checkbox" />
        <span>{{ payload.secretToggleLabel || '附带隐藏内容' }}</span>
      </label>
      <textarea class="sk-preview" readonly :value="preview" />
      <div class="sk-channels">
        <button
          v-for="link in links"
          :key="link.id"
          type="button"
          class="sk-channel"
          :data-channel="link.id"
          @click="onChannel(link.id, link.href)"
        >
          {{ link.label }}
        </button>
      </div>
      <div v-if="wechatOpen" class="sk-wechat">
        <template v-if="wechatMode === 'in-app'">
          点击右上角 ··· ，发送给朋友或分享到朋友圈。链接卡片使用网页简介。
        </template>
        <template v-else-if="wechatMode === 'desktop'">
          <canvas ref="qrCanvas" width="180" height="180" />
          <p>微信扫一扫，打开后即可转发给好友。</p>
        </template>
        <template v-else>
          保存下方海报发到朋友圈，或先复制文案再到微信粘贴。
        </template>
      </div>
      <img v-if="posterUrl" class="sk-poster" :src="posterUrl" alt="分享海报，长按可保存" />
      <footer class="sk-actions">
        <button type="button" class="sk-primary" @click="onCopy">复制文案与链接</button>
        <button type="button" class="sk-secondary" @click="posterUrl ? downloadPoster() : onPoster()">
          {{ posterBusy ? '正在生成…' : posterUrl ? '保存高清海报' : '生成海报' }}
        </button>
        <button v-if="canNative" type="button" class="sk-secondary" @click="onNative">系统分享</button>
      </footer>
      <p v-if="toast" class="sk-toast" role="status">{{ toast }}</p>
    </div>
  </div>
</template>

<style scoped>
.sk-mask {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(28, 25, 23, 0.45);
  padding: 16px;
  font-family: var(--sk-font);
}
.sk-dialog {
  width: min(440px, 100%);
  max-height: min(88vh, 760px);
  overflow: auto;
  background: var(--sk-surface);
  color: var(--sk-text);
  border: 1px solid var(--sk-border);
  border-radius: 16px;
  padding: 16px 16px 12px;
  box-shadow: 0 16px 40px rgba(28, 25, 23, 0.18);
}
.sk-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.sk-head h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}
.sk-icon {
  border: 0;
  background: transparent;
  color: var(--sk-muted);
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}
.sk-notice {
  margin: 8px 0 12px;
  color: var(--sk-muted);
  font-size: 12px;
  line-height: 1.5;
}
.sk-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 14px;
}
.sk-preview {
  width: 100%;
  min-height: 96px;
  box-sizing: border-box;
  resize: vertical;
  border: 1px solid var(--sk-border);
  border-radius: 10px;
  background: var(--sk-bg);
  color: var(--sk-text);
  padding: 10px;
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
}
.sk-channels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0;
}
.sk-channel {
  border: 1px solid var(--sk-border);
  background: var(--sk-bg);
  color: var(--sk-text);
  border-radius: 999px;
  padding: 6px 12px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}
.sk-channel[data-channel='wechat'] { color: #128c3e; }
.sk-channel[data-channel='qq'] { color: #1296db; }
.sk-channel[data-channel='weibo'] { color: #e6162d; }
.sk-wechat {
  margin-bottom: 12px;
  padding: 10px;
  border-radius: 10px;
  background: var(--sk-bg);
  color: var(--sk-muted);
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}
.sk-wechat canvas {
  display: block;
  margin: 0 auto 8px;
  width: 180px;
  height: 180px;
}
.sk-poster {
  display: block;
  width: min(240px, 70%);
  margin: 0 auto 12px;
  border-radius: 8px;
  border: 1px solid var(--sk-border);
}
.sk-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.sk-primary,
.sk-secondary {
  border-radius: 8px;
  padding: 8px 12px;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}
.sk-primary {
  border: 0;
  background: var(--sk-primary);
  color: #fff;
}
.sk-secondary {
  border: 1px solid var(--sk-primary);
  background: transparent;
  color: var(--sk-primary);
}
.sk-toast {
  margin: 10px 0 0;
  color: var(--sk-accent);
  font-size: 13px;
}
@media (min-width: 640px) {
  .sk-mask { align-items: center; }
}
</style>
